import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Coverage } from "../api/types";

/**
 * The honesty component, and the panel's stat tile.
 *
 * The API marks each dashboard metric with where its number comes from, and this
 * renders that. A metric whose producer does not exist yet is struck through and
 * annotated with the reason, instead of being shown as a plain `0`.
 *
 * The distinction matters more than it looks. "0 inquiries" is a real business
 * signal — it says buyers looked and did not write. "0 inquiries" as an artefact
 * of an unbuilt chat module says nothing at all, and a dealer reading it would
 * draw a false conclusion about their inventory. Greying the number out is not
 * enough; grey still reads as a value. So the `data-pending` attribute in
 * index.css strikes it through, and the caption names the module that owes the
 * number.
 */

const PENDING_REASON: Record<Exclude<Coverage, "live">, string> = {
  awaiting_module: "no data yet — the module that records this is not built",
  not_computed: "not computed — its input does not exist yet",
};

export function CoverageBadge({ coverage }: { coverage: Coverage }) {
  if (coverage === "live") {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-medium uppercase tracking-[0.06em] text-ink-faint">
        measured
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-signal-wash px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-signal-ink"
      title={PENDING_REASON[coverage]}
    >
      {coverage === "awaiting_module" ? "not wired" : "not computed"}
    </span>
  );
}

/**
 * "Needs your attention" — the work queue.
 *
 * Deliberately the one place the amber accent appears. A queue rendered in the
 * same ink as everything else reads as a styling choice; the amber is reserved
 * for "you have work here" and nothing else, so its presence is information.
 */
export interface WorkQueueItem {
  label: string;
  detail?: string;
  count: number;
}

export function WorkQueue({ items }: { items: WorkQueueItem[] }) {
  if (items.length === 0) {
    return (
      <p className="py-2 text-sm text-ink-muted">
        Nothing needs your attention.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-line">
      {items.map((item) => (
        <li key={item.label} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
          {/* The indicator lamp. */}
          <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-signal" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">{item.label}</p>
            {item.detail ? (
              <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{item.detail}</p>
            ) : null}
          </div>
          <span className="tnum shrink-0 text-sm font-semibold text-signal-ink">{item.count}</span>
        </li>
      ))}
    </ul>
  );
}

export function Metric({
  label,
  value,
  coverage = "live",
  hint,
  className,
}: {
  label: string;
  value: string | number | null;
  coverage?: Coverage;
  hint?: ReactNode;
  className?: string;
}) {
  const pending = coverage !== "live";

  return (
    <div className={cn("surface p-5 transition-shadow hover:shadow-md", className)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-ink-muted">{label}</span>
        <CoverageBadge coverage={coverage} />
      </div>

      <div
        className="tnum mt-3 text-[28px] font-semibold leading-none tracking-tight text-ink"
        // Struck through when the number is an artefact of unbuilt code, so it
        // cannot be read as a measurement.
        data-pending={pending ? "true" : undefined}
      >
        {value === null ? "—" : value}
      </div>

      {pending ? (
        <p className="mt-2 text-[11px] leading-relaxed text-ink-faint">
          {PENDING_REASON[coverage as Exclude<Coverage, "live">]}
        </p>
      ) : hint ? (
        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}
